/**
 * Role-Protected Route Component
 *
 * Wrapper component for route-level access control based on user role permissions.
 * Redirects unauthorized users to a specified fallback route.
 *
 * @module components/auth/RoleProtectedRoute
 */

import { Navigate } from 'react-router-dom';
import { usePermissions, type Permissions } from '@/hooks';
import { Spinner } from '@/components/ui';

// =============================================================================
// Types
// =============================================================================

export interface RoleProtectedRouteProps {
  /** Content to render if user has required permission */
  children: React.ReactNode;

  /** Function that checks if user has required permission */
  requiredPermission: (permissions: Permissions) => boolean;

  /** Route to redirect to if permission check fails (default: /overview) */
  redirectTo?: string;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Protects a route based on user role permissions.
 *
 * @example
 * ```tsx
 * <Route
 *   path="billing"
 *   element={
 *     <RoleProtectedRoute
 *       requiredPermission={(p) => p.canAccessBilling}
 *       redirectTo="/settings/organization"
 *     >
 *       <BillingPage />
 *     </RoleProtectedRoute>
 *   }
 * />
 * ```
 */
export function RoleProtectedRoute({
  children,
  requiredPermission,
  redirectTo = '/overview',
}: RoleProtectedRouteProps) {
  const permissions = usePermissions();

  // Show loading state while permissions are being determined
  if (permissions.loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Spinner size="lg" />
      </div>
    );
  }

  // Check if user has required permission
  const hasPermission = requiredPermission(permissions);

  if (!hasPermission) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
